'use client';

import { useState, useMemo, useEffect } from 'react';

/* ── Types ── */
interface Change {
  changeType: 'added' | 'improved' | 'updated' | 'fixed' | 'compatibility';
  description: string;
}
interface Release {
  version: string;
  date: string;
  changes: Change[];
}

/* ── Badge config ── */
const TYPE_META: Record<string, { label: string; bg: string; text: string; dot: string }> = {
  added:         { label: 'Added',         bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
  improved:      { label: 'Improved',      bg: 'bg-violet-50',  text: 'text-violet-700',  dot: 'bg-violet-500' },
  updated:       { label: 'Updated',       bg: 'bg-sky-50',     text: 'text-sky-700',     dot: 'bg-sky-500' },
  fixed:         { label: 'Fixed',         bg: 'bg-rose-50',    text: 'text-rose-700',    dot: 'bg-rose-500' },
  compatibility: { label: 'Compatibility', bg: 'bg-amber-50',   text: 'text-amber-700',   dot: 'bg-amber-500' },
};

const FILTER_ALL = 'all';
const INITIAL_VISIBLE = 5;

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Format an ISO date (YYYY-MM-DD) with no Date()/timezone, so it renders identically
 *  on server and client (no hydration mismatch). Returns null for non-ISO values. */
function absoluteDate(raw: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  return m ? `${MONTHS[+m[2] - 1]} ${+m[3]}, ${m[1]}` : null;
}

/** Live "x ago": plain DAYS for anything under 60 days (unambiguous — no confusing
 *  week rounding), then whole MONTHS, then YEARS. Rounds the raw elapsed time.
 *  Client-only (needs a resolved `now`), so it never goes stale nor mismatches on
 *  hydration. Returns null for non-ISO values. */
function relativeDate(raw: string, now: number): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!m) return null;
  const diff = Math.abs(now - Date.UTC(+m[1], +m[2] - 1, +m[3])) / 1000; // seconds
  const DAY = 86_400, MONTH = 2_592_000 /* 30d */, YEAR = 31_536_000 /* 365d */;
  const ago = (value: number, unit: string) => {
    const n = Math.max(1, value);
    return `${n} ${unit}${n > 1 ? 's' : ''} ago`;
  };
  const days = Math.floor(diff / DAY);           // completed days, so noon ≠ +1 day
  if (days <= 0) return 'today';
  if (days < 60) return ago(days, 'day');
  const months = Math.round(diff / MONTH);
  if (months < 12) return ago(months, 'month'); // 60 days → 2 months, up to 11 months
  return ago(Math.round(diff / YEAR), 'year');   // ~345+ days → 1 year, no "12 months"
}

/** Resolves to Date.now() only after mount → relative time is computed client-side. */
function useNow(): number | null {
  const [now, setNow] = useState<number | null>(null);
  useEffect(() => { setNow(Date.now()); }, []);
  return now;
}

/* ── Change badge ── */
function Badge({ type }: { type: string }) {
  const m = TYPE_META[type] || TYPE_META.added;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold ${m.bg} ${m.text} whitespace-nowrap`}>
      <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
}

/* ── Expand/collapse chevron ── */
function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      className={`w-5 h-5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
      fill="none" viewBox="0 0 24 24" stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

/* ── Release card ── */
function ReleaseCard({ release, index, expandedSet, toggle, filter, now }: {
  release: Release;
  index: number;
  expandedSet: Set<number>;
  toggle: (i: number) => void;
  filter: string;
  now: number | null;
}) {
  const isOpen = expandedSet.has(index);
  const filtered = filter === FILTER_ALL
    ? release.changes
    : release.changes.filter(c => c.changeType === filter);

  // Count by type
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const ch of release.changes) c[ch.changeType] = (c[ch.changeType] || 0) + 1;
    return c;
  }, [release.changes]);

  if (filter !== FILTER_ALL && filtered.length === 0) return null;

  return (
    <div className="relative pl-8 md:pl-12 pb-10 group">
      {/* Timeline line */}
      <div className="absolute left-[11px] md:left-[19px] top-3 bottom-0 w-px bg-stone-200 group-last:hidden" />
      {/* Timeline dot */}
      <div
        className="absolute left-1 md:left-2.5 top-2.5 w-4 h-4 md:w-5 md:h-5 rounded-full border-[3px] border-white shadow-sm"
        style={{ backgroundColor: index === 0 ? 'var(--color-t-cta, #3b82f6)' : '#d1d5db' }}
      />

      {/* Header */}
      <button
        onClick={() => toggle(index)}
        className="w-full flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-left cursor-pointer group/btn"
      >
        <div className="flex items-center gap-3">
          <h3 className="text-lg md:text-xl font-bold text-stone-900">
            v{release.version}
          </h3>
          <span className="text-sm text-stone-400 font-medium">
            {absoluteDate(release.date) ?? release.date}
            {now !== null && relativeDate(release.date, now) && (
              <span className="text-stone-300"> · {relativeDate(release.date, now)}</span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2 sm:ml-auto">
          {/* Mini count badges */}
          {Object.entries(counts).map(([type, count]) => {
            const m = TYPE_META[type];
            if (!m) return null;
            return (
              <span key={type} className={`inline-flex items-center gap-1 text-xs font-medium ${m.text} opacity-70`}>
                <span className={`w-1.5 h-1.5 rounded-full ${m.dot}`} />
                {count}
              </span>
            );
          })}
          <span className="text-stone-400 ml-1">
            <Chevron open={isOpen} />
          </span>
        </div>
      </button>

      {/* Changes list */}
      {isOpen && (
        <div className="mt-4 space-y-2.5">
          {filtered.map((change, ci) => (
            <div
              key={ci}
              className="flex items-start gap-3 p-3 rounded-lg bg-white border border-stone-100 hover:border-stone-200 transition-colors"
            >
              <Badge type={change.changeType} />
              <p className="text-sm text-stone-700 leading-relaxed flex-1">
                {change.description}
              </p>
            </div>
          ))}
          {filtered.length === 0 && (
            <p className="text-sm text-stone-400 italic pl-1">No {filter} changes in this release.</p>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
 * RELEASE NOTES BLOCK RENDERER
 * ═══════════════════════════════════════════════════════════════ */
export function ReleaseNotesBlockRenderer({ block }: { block: any }) {
  const releases: Release[] = useMemo(() => block.releases || [], [block.releases]);
  const [filter, setFilter] = useState(FILTER_ALL);
  const [showAll, setShowAll] = useState(false);
  const [expandedSet, setExpandedSet] = useState<Set<number>>(() => {
    // Expand first 3 releases by default
    const s = new Set<number>();
    for (let i = 0; i < Math.min(3, releases.length); i++) s.add(i);
    return s;
  });

  const toggle = (i: number) => {
    setExpandedSet(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  // Total counts
  const totalCounts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const r of releases) for (const ch of r.changes) c[ch.changeType] = (c[ch.changeType] || 0) + 1;
    return c;
  }, [releases]);

  const totalChanges = Object.values(totalCounts).reduce((a, b) => a + b, 0);

  const visible = showAll ? releases : releases.slice(0, INITIAL_VISIBLE);

  const isAlt = ((block._sectionIndex ?? 0) % 2) === 1;
  const now = useNow();

  return (
    <section className="py-16 md:py-24" style={{ backgroundColor: isAlt ? 'var(--color-t-section-alt)' : 'var(--color-t-section-light)' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-stone-900 mb-3">
            {block.heading || "What's New in OfficePortal"}
          </h1>
          {block.subheading && (
            <p className="text-base sm:text-lg text-stone-500 max-w-2xl mx-auto">
              {block.subheading}
            </p>
          )}
          <div className="flex items-center justify-center gap-4 mt-4 text-sm text-stone-400">
            <span>{releases.length} releases</span>
            <span className="w-1 h-1 rounded-full bg-stone-300" />
            <span>{totalChanges} changes</span>
          </div>
        </div>

        {/* Filter bar */}
        <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
          <FilterBtn active={filter === FILTER_ALL} onClick={() => setFilter(FILTER_ALL)}>
            All
          </FilterBtn>
          {(['added', 'improved', 'updated', 'fixed', 'compatibility'] as const).map(type => {
            const m = TYPE_META[type];
            const count = totalCounts[type] || 0;
            if (count === 0) return null;
            return (
              <FilterBtn key={type} active={filter === type} onClick={() => setFilter(type)}>
                <span className={`w-2 h-2 rounded-full ${m.dot}`} />
                {m.label}
                <span className="text-stone-400 ml-0.5">{count}</span>
              </FilterBtn>
            );
          })}
        </div>

        {/* Timeline */}
        <div className="relative">
          {visible.map((release, i) => (
            <ReleaseCard
              key={release.version}
              release={release}
              index={i}
              expandedSet={expandedSet}
              toggle={toggle}
              filter={filter}
              now={now}
            />
          ))}
        </div>

        {/* Show more */}
        {releases.length > INITIAL_VISIBLE && !showAll && (
          <div className="text-center mt-8">
            <button
              onClick={() => setShowAll(true)}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-white border border-stone-200 text-sm font-medium text-stone-600 hover:bg-stone-50 hover:border-stone-300 transition-colors cursor-pointer"
            >
              Show all {releases.length} releases
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

/* ── Filter button ── */
function FilterBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
        active
          ? 'bg-stone-900 text-white shadow-sm'
          : 'bg-white text-stone-600 border border-stone-200 hover:border-stone-300 hover:bg-stone-50'
      }`}
    >
      {children}
    </button>
  );
}
